import { ListenableEvent, Log } from "../../utils";
import { forEach } from "../../utils/async";
import { ERR_INVALID_ARGUMENT, ERR_INVALID_STATE, ERR_NOT_AVAILABLE, rejectError } from "../../utils/error";
export class CommandManager {
    get canUndo() {
        return (this.mUndoSections.length > 0);
    }
    get canRedo() {
        return (this.mRedoSections.length > 0);
    }
    get recordingEnabled() {
        return (this.mRecordingEnabled);
    }
    set recordingEnabled(pEnabled) {
        this.mRecordingEnabled = pEnabled;
    }
    get historySize() {
        return (this.mHistorySize);
    }
    constructor() {
        /**
         * Will be triggered if the undo/redo history has changed.
         */
        this.eventHistoryChanged = new ListenableEvent(this);
        this.mCurrentSection = null;
        this.mSectionDepth = 0;
        this.mUndoSections = [];
        this.mRedoSections = [];
        this.mRecordingEnabled = false;
        this.mHistorySize = 10;
    }
    executeCommand(pCommand, pCommandParameter = null) {
        if (pCommand == null)
            return (Promise.reject(new Error(ERR_INVALID_ARGUMENT)));
        if (!pCommand.isAvailable())
            return (Promise.reject(new Error(ERR_NOT_AVAILABLE)));
        const tPromise = pCommand.execute(pCommandParameter).then((pExecuted) => {
            if (pExecuted)
                this.addCommandToHistory(pCommand);
            return (pExecuted);
        });
        return (tPromise);
    }
    beginUndoSection() {
        if (this.mSectionDepth === 0)
            this.mCurrentSection = new UndoSection();
        this.mSectionDepth++;
    }
    endUndoSection() {
        this.mSectionDepth--;
        if (this.mSectionDepth <= 0) {
            if (this.mCurrentSection != null && !this.mCurrentSection.isEmpty) {
                this.mUndoSections.push(this.mCurrentSection);
                //clear redo sections
                this.internal_clearHistory(false, true, false);
                this.ensureHistorySize();
                this.eventHistoryChanged.trigger();
            }
            this.mCurrentSection = null;
            this.mSectionDepth = 0;
        }
    }
    undo() {
        if (!this.canUndo || this.mCurrentSection != null)
            return (rejectError(ERR_INVALID_STATE));
        const tSection = this.mUndoSections.pop();
        const tPromise = tSection.undo().then(() => {
            this.mRedoSections.push(tSection);
            this.eventHistoryChanged.trigger();
        });
        return (tPromise);
    }
    redo() {
        if (!this.canRedo || this.mCurrentSection != null)
            return (rejectError(ERR_INVALID_STATE));
        const tSection = this.mRedoSections.pop();
        const tPromise = tSection.redo().then(() => {
            this.mUndoSections.push(tSection);
            this.eventHistoryChanged.trigger();
        });
        return (tPromise);
    }
    clearHistory() {
        this.internal_clearHistory(true, true, true);
        this.eventHistoryChanged.trigger();
    }
    setHistorySize(pSize) {
        if (pSize == null || pSize <= 0 || pSize === this.mHistorySize)
            return;
        this.mHistorySize = pSize;
        this.ensureHistorySize();
        this.eventHistoryChanged.trigger();
    }
    addCommandToHistory(pCommand) {
        if (!this.mRecordingEnabled) {
            pCommand.dispose();
            return;
        }
        if (pCommand.isUndoable()) {
            //start a section if there is currently no section
            if (this.mCurrentSection == null) {
                this.beginUndoSection();
                this.mCurrentSection.addCommand(pCommand);
                this.endUndoSection();
            }
            else
                this.mCurrentSection.addCommand(pCommand);
        }
        else {
            //not undoable but significant -> clear history
            if (pCommand.isSignificant()) {
                this.clearHistory();
            }
        }
    }
    internal_clearHistory(pClearUndo, pClearRedo, pClearCurrentSection) {
        if (pClearUndo) {
            for (const tSection of this.mUndoSections)
                tSection.dispose();
            this.mUndoSections = [];
        }
        if (pClearRedo) {
            for (const tSection of this.mRedoSections)
                tSection.dispose();
            this.mRedoSections = [];
        }
        if (pClearCurrentSection && this.mCurrentSection != null)
            this.mCurrentSection.clear();
    }
    ensureHistorySize() {
        if (this.mUndoSections.length <= this.mHistorySize)
            return;
        const tNumRemove = this.mUndoSections.length - this.mHistorySize;
        for (let i = 0; i < tNumRemove; ++i)
            this.mUndoSections[i].dispose();
        this.mUndoSections.splice(0, tNumRemove);
    }
}
class UndoSection {
    get isEmpty() {
        return (this.mCommands.length === 0);
    }
    constructor() {
        this.mCommands = [];
    }
    dispose() {
        this.disposeCommands();
        this.mCommands = undefined;
    }
    clear() {
        this.disposeCommands();
        this.mCommands = [];
    }
    addCommand(pCommand) {
        this.mCommands.push(pCommand);
    }
    undo() {
        const tPromise = forEach(this.mCommands, true, (pIndex, pCommand) => {
            const tUndoPromise = pCommand.undo().then(() => {
                return (true);
            }).catch((pError) => {
                Log.error("Failed to undo command.", pError);
                return (true);
            });
            return (tUndoPromise);
        });
        return (tPromise.then(() => { }));
    }
    redo() {
        const tPromise = forEach(this.mCommands, false, (pIndex, pCommand) => {
            const tRedoPromise = pCommand.redo().then(() => {
                return (true);
            }).catch((pError) => {
                Log.error("Failed to redo command.", pError);
                return (true);
            });
            return (tRedoPromise);
        });
        return (tPromise.then(() => { }));
    }
    disposeCommands() {
        for (const tCommand of this.mCommands)
            tCommand.dispose();
    }
}
